import { Experience } from './Experience';
import { type ExperienceEvent } from './ExperienceEvent';

export type ExperienceEventSubscriber = (event: ExperienceEvent) => void;
export type ExperienceEventListenerUnsubscribe = () => void;
export type CollectFn = (events: ExperienceEvent[], experience: Experience) => void;

export type StartOptions = {
	attributes?: object;
	collect?: CollectFn;
	id: string;
	name: string;
	onAbort?: () => void;
	onFailure?: () => void;
	onSuccess?: () => void;
	startTime?: number;
	timeout?: number;
};

export type SucceedOptions = {
	attributes?: object;
	name: string;
};

export type FailOptions = {
	attributes?: object;
	error: Error;
	name?: string;
};

export type AbortOptions = {
	attributes?: object;
	checkForTimeout?: boolean;
	name?: string;
	reason: string;
};

export type StopOnErrorOptions = {
	attributes?: object;
	error: Error;
	name: string;
};

type UpdateAttributesOptions = {
	attributes: object;
	name: string;
};

export interface ExperienceTrackerAPI {
	abort(options: AbortOptions): void;
	fail(options: FailOptions): void;
	start(options: StartOptions): void;
	/**
	 * Fails or aborts an experience with a specific `name` because of a specific
	 * `error` based on product-specific logic. For example, if the specified
	 * `error` describes a network error or a user error (these are internal
	 * implementation details so do not rely that any particular classification of
	 * errors is really implemented), `ExperienceStop` may abort the experience with
	 * the specified `name` rather than fail it because the errors in question are
	 * not product failures.
	 */
	stopOnError(options: StopOnErrorOptions): void;

	subscribe(subscriber: ExperienceEventSubscriber): ExperienceEventListenerUnsubscribe;

	succeed(options: SucceedOptions): void;

	/**
	 * Attributes that are set at the start of the actively running experience or
	 * using this updateAttributes method will be set on all subsequent experience events.
	 *
	 * Q: When should you use updateAttributes?
	 * A: If you do not have easy access to setting an attribute at the start of an experience,
	 *    it may make sense to call updateAttributes on the actively running experience to ensure that
	 *    all subsequent experience events have that attribute.
	 *    This is helpful to ensure that if your experience fails in an unexpected way, you guarantee
	 *    that all experience fails/aborts will have the attributes you expect.
	 */
	updateAttributes(options: UpdateAttributesOptions): void;
}

export class ExperienceTracker implements ExperienceTrackerAPI {
	private experiences: {
		[name: string]: Experience;
	} = {};
	private subscribers: ExperienceEventSubscriber[] = [];
	private isNetworkOfflineError: (error: Error) => boolean;

	constructor(options?: { isNetworkOfflineError(error: Error): boolean }) {
		this.isNetworkOfflineError = (options && options.isNetworkOfflineError) || (() => false);
	}

	start({
		name,
		id,
		timeout,
		startTime,
		attributes,
		onSuccess,
		onFailure,
		onAbort,
		collect,
	}: StartOptions): void {
		const current = this.experiences[name];
		if (current && !current.hasStopped && current.id === id) {
			return;
		}

		// Before replacing a current experience, abort it to ensure
		// it is cleaned up.
		if (current) {
			current.abort({
				reason: 'Aborted because the same experience was started with a new id',
			});
		}

		const experience = new Experience({
			name,
			id,
			timeout,
			startTime,
			attributes,
			onSuccess,
			onFailure,
			onAbort,
			onStart: (startEvent) => {
				this.emit(startEvent);

				let disposeCollectSubscription = () => {};
				if (collect) {
					const events: ExperienceEvent[] = [];
					disposeCollectSubscription = this.subscribe((event) => {
						// Ignore already running experiences that started before this
						// experience started
						if (
							event.action === 'taskStart' ||
							events.some(
								(previousEvent) =>
									previousEvent.name === event.name && previousEvent.action === 'taskStart',
							)
						) {
							events.push(event);
							collect(events, experience);
						}
					});
				}

				return (stopEvent) => {
					disposeCollectSubscription();
					this.emit(stopEvent);
				};
			},
		});

		this.experiences[name] = experience;
	}

	succeed({ name, attributes }: SucceedOptions): void {
		const current = this.experiences[name];
		if (!current) {
			return;
		}

		current.succeed(attributes);
	}

	fail({ name, error, attributes }: FailOptions): void {
		Object.values(this.experiences).forEach((current) => {
			if (name != null && current.name !== name) {
				return;
			}

			current.fail({ error, attributes });
		});
	}

	abort({ name, reason, attributes, checkForTimeout }: AbortOptions): void {
		function abort(experience: Experience) {
			if (name != null && experience.name !== name) {
				return;
			}

			experience.abort({ reason, checkForTimeout, attributes });
		}

		const experiencesWithTimeouts = Object.values(this.experiences).filter(
			(experience) => experience.timeout,
		);
		const experiencesWithoutTimeouts = Object.values(this.experiences).filter(
			(experience) => !experience.timeout,
		);

		// We need to abort all experiences with timeouts first in order to properly
		// capture compound experiences composed of sub-experiences which have timeouts
		experiencesWithTimeouts.forEach(abort);
		experiencesWithoutTimeouts.forEach(abort);
	}

	subscribe(subscriber: ExperienceEventSubscriber): () => void {
		if (typeof subscriber !== 'function') {
			throw new Error('Subscriber must be a function');
		}
		this.subscribers.push(subscriber);
		return () => {
			this.subscribers = this.subscribers.filter((x) => x !== subscriber);
		};
	}

	stopOnError({ name, error, attributes }: StopOnErrorOptions): void {
		if (this.isNetworkOfflineError(error)) {
			this.abort({
				name,
				reason: `Aborted ${name} because of network error: ${error.toString()}`,
				checkForTimeout: false,
				attributes,
			});
			return;
		}

		return this.fail({ name, error, attributes });
	}

	updateAttributes({ name, attributes }: UpdateAttributesOptions): void {
		const current = this.experiences[name];
		if (!current) {
			return;
		}

		current.updateAttributes(attributes);
	}

	private emit(event: ExperienceEvent) {
		this.subscribers.forEach((subscriber) => {
			try {
				subscriber(event);
			} catch (e) {
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.error(
						`Error occurred in ExperienceTracker subscriber when handling an event.`,
						'Error:',
						e,
						'Event:',
						event,
					);
				}
			}
		});
	}
}
